'use client'

import React from 'react'
import Link from 'next/link'
import { useLanguage } from '../../providers/LanguageProvider'
import clsx from 'clsx'
import Image from 'next/image'

type SubHeroText = {
  en: React.ReactNode
  ar: React.ReactNode
}

interface SubHeroContent {
  title: SubHeroText
  paragraph: SubHeroText
}

interface SubHeroSectionProps {
  backgroundimage: string
  alt?: string
  content: SubHeroContent
  button?: React.ReactNode
  buttonHref?: string
  containerClassName?: string
  paragraphWidthClassName?: string
  headingWidthClassName?: string
  locale: 'en' | 'ar'
}

export const SubHeroSection: React.FC<SubHeroSectionProps> = ({
  locale,
  backgroundimage,
  alt,
  content,
  button,
  buttonHref = '#',
  containerClassName = 'nav-container xl:pb-24 2xl:pb-36 ',
  paragraphWidthClassName = 'w-full md:w-[80%] lg:w-[70%] xl:w-[60%]',
  headingWidthClassName = 'w-full md:w-[80%] lg:w-[70%] xl:w-[60%]',
}) => {
  const isArabic = locale === 'ar'

  const title = isArabic ? content.title.ar : content.title.en
  const paragraph = isArabic ? content.paragraph.ar : content.paragraph.en

  return (
    <section className="relative w-full min-h-[60dvh] sm:h-[500px] md:h-[530px] lg:h-[600px] xl:h-[800px] 2xl:h-[900px] overflow-hidden">
      <Image    src={backgroundimage} alt="CTA Background" fill priority className="object-cover object-center" />

      <div className="absolute inset-0 z-0">
        <div className="w-full h-full bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
      </div>

      {/* Content aligned to bottom on md+, center on mobile */}
      <div className="relative w-full z-10 flex flex-col justify-end h-[60dvh] sm:h-[500px] md:h-[530px] lg:h-[600px] xl:h-[800px] 2xl:h-[900px]">
        <div
          className={clsx(
            containerClassName,
            ` mx-auto flex flex-col gap-4 md:gap-5 md:px-0 pt-32 md:pt-0 lg:px-12 lg:pb-20 xl:px-0 pb-10 md:pb-16 md:text-left items-start `,
          )}
        >
          <h1
            className={clsx(
              'text-white font-[400] leading-snug capitalize',
              headingWidthClassName,
              isArabic
                ? 'text-[24px] sm:text-[26px] md:text-[30px] lg:text-[42px] xl:text-[50px] text-right'
                : 'text-[24px] sm:text-[24px] md:text-[30px] lg:text-[42px] xl:text-[50px]',
            )}
          >
            {title}
          </h1>

          <p
            className={clsx(
              'text-white font-light leading-relaxed ',
              paragraphWidthClassName,
              isArabic
                ? 'text-[14px] sm:text-[15px] md:text-[16px] xl:text-[20px] text-right'
                : 'text-[14px] sm:text-[14px] md:text-[14px] xl:text-[20px] text-left',
            )}
          >
            {paragraph}
          </p>

          {button && (
            <div className={`mt-3 ${isArabic ? '-mr-[12px] md:mr-[0]' : '-ml-[12px] md:ml-[0]'}`}>
              <Link href={buttonHref} className="inline-block">
                {button}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
